import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  TextField,
  Divider,
  Link,
  CssBaseline,
  ThemeProvider,
  createTheme,
  useMediaQuery,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  FormHelperText,
  IconButton,
} from "@mui/material";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { Formik, Form, useField } from "formik";
import * as Yup from "yup";
import bannerImage from "../../assets/banner.jpeg";
import buyerImage from "../../assets/buy_scrap_online.jpeg";
import sellerImage from "../../assets/sell_scrap_online.jpeg";
import { loginUser, resendVerification } from "../../features/auth/authSlice";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getFcmToken } from "../../utils/token";
import { showAppMessageToast } from "../../utils/toastUtils";
import {
  clearUserProfile,
  fetchUserProfile,
} from "../../features/user/userSlice";
import EditIcon from "@mui/icons-material/Edit";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { requestOtp, clearError } from "../../features/auth/authSlice";
import { getSellerAccess } from "../../utils/getSellerRedirect";

// Login step constants
const LOGIN_STEP = {
  EMAIL_ROLE: "EMAIL_ROLE",
  CHOOSE_METHOD: "CHOOSE_METHOD",
  PASSWORD: "PASSWORD",
};

const ROLE_CONTENT = {
  buyer: {
    title: "Buy Scrap Smarter",
    subtitle: "Source quality scrap from verified sellers.",
    tagline: "Discover the best scrap deals with trust.",
    image: buyerImage,
  },
  seller: {
    title: "Sell Scrap Faster",
    subtitle: "Reach verified buyers & grow your business.",
    tagline: "Turn your scrap into profit effortlessly.",
    image: sellerImage,
  },
};

// Custom theme to match the colors from the image
const customTheme = createTheme({
  palette: {
    primary: {
      main: "#1976d2", // A common blue for links
    },
    secondary: {
      main: "#ff9800", // The orange/yellow color for the OTP button
      contrastText: "#fff",
    },
    scraptradeYellow: {
      main: "#F4C542", // Updated color as requested
      dark: "#E3AE27",
    },
    scraptradeDark: {
      main: "#2D2D2D",
    },
    background: {
      default: "#f4f6f8",
    },
  },
  typography: {
    fontFamily: "'Inter', sans-serif",
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: "8px",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
          paddingTop: "12px", // Increased padding as requested
          paddingBottom: "12px", // Increased padding as requested
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: "16px",
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: "#f0f4f9",
          borderRadius: "8px",
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#F4C542",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#E3AE27",
          },
          // New style to target placeholder text on focus
          "&.Mui-focused .MuiInputBase-input::placeholder": {
            color: "#E3AE27", // Use a dark yellow for better contrast
            opacity: 1, // Ensure it is fully visible
          },
          // New style to target the label on focus
          "&.Mui-focused .MuiInputLabel-root": {
            color: "#E3AE27",
          },
        },
        notchedOutline: {
          borderColor: "transparent",
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          "&.Mui-focused": {
            color: "#E3AE27",
          },
        },
      },
    },
  },
});

// A custom Formik-compatible TextField component
const FormikTextField = ({ label, readOnly, onEditClick, type, ...props }) => {
  const [field, meta] = useField(props);
  const [showPassword, setShowPassword] = useState(false);
  const errorText = meta.error && meta.touched ? meta.error : "";

  const isPassword = type === "password";

  return (
    <TextField
      {...props}
      {...field}
      label={label}
      fullWidth
      margin="normal"
      variant="outlined"
      type={isPassword ? (showPassword ? "text" : "password") : type}
      error={!!errorText}
      helperText={errorText}
      InputProps={{
        readOnly: readOnly,
        endAdornment: isPassword ? (
          <IconButton
            onClick={() => setShowPassword(!showPassword)}
            size="small"
            edge="end"
          >
            {showPassword ? <VisibilityOff /> : <Visibility />}
          </IconButton>
        ) : readOnly && onEditClick ? (
          <IconButton onClick={onEditClick} size="small" edge="end">
            <EditIcon />
          </IconButton>
        ) : null,
      }}
      sx={{
        "& .MuiOutlinedInput-root": readOnly
          ? {
            backgroundColor: "#e8edf2",
            "& fieldset": {
              borderColor: "#d0d7de",
            },
          }
          : {},
      }}
    />
  );
};

// Conditional validation schema based on step
const getValidationSchema = (step) => {
  switch (step) {
    case LOGIN_STEP.EMAIL_ROLE:
      return Yup.object({
        email: Yup.string().email("Invalid email").required("Email required"),
        role: Yup.string().required("Please select a role"),
      });
    case LOGIN_STEP.PASSWORD:
      return Yup.object({
        email: Yup.string().email("Invalid email").required("Email required"),
        role: Yup.string().required("Please select a role"),
        password: Yup.string()
          .min(6, "Minimum 6 characters")
          .required("Password required"),
      });
    default:
      return Yup.object({
        email: Yup.string().email("Invalid email").required("Email required"),
        role: Yup.string().required("Please select a role"),
      });
  }
};

const Login = () => {
  const isMobile = useMediaQuery(customTheme.breakpoints.down("md"));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useSelector((state) => state.auth);

  // Clear any existing auth errors on mount
  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  // Step state
  const [loginStep, setLoginStep] = useState(LOGIN_STEP.EMAIL_ROLE);

  // Get preselected role from location state (from Buy/Sell pages)
  const preselectedRole = location.state?.role || "buyer";
  console.log("preselectedRole in login:", preselectedRole);

  // Track selected role for dynamic banner text
  const [selectedRole, setSelectedRole] = useState(preselectedRole);
  const roleContent = ROLE_CONTENT[selectedRole] || ROLE_CONTENT.buyer;

  useEffect(() => {
    setSelectedRole(preselectedRole);
  }, [preselectedRole]);

  const handleContinueToMethod = (values, { setTouched }) => {
    // Validate email and role before proceeding
    const schema = getValidationSchema(LOGIN_STEP.EMAIL_ROLE);
    schema
      .validate(values, { abortEarly: false })
      .then(() => {
        setLoginStep(LOGIN_STEP.CHOOSE_METHOD);
      })
      .catch((err) => {
        // Set touched for all fields to show errors
        const touched = {};
        err.inner.forEach((error) => {
          touched[error.path] = true;
        });
        setTouched(touched);
      });
  };

  const handleChoosePassword = () => {
    setLoginStep(LOGIN_STEP.PASSWORD);
  };

  const handleChooseOTP = async (values) => {
    try {
      const payload = {
        email: values.email,
        selected_role: preselectedRole,
      };

      await dispatch(requestOtp(payload)).unwrap();

      showAppMessageToast({
        type: "success",
        title: "OTP Sent",
        message: "A one-time password has been sent to your email.",
      });

      navigate("/request-otp", {
        state: {
          email: values.email,
          role: values.role,
          source: "login",
        },
      });
    } catch (err) {
      showAppMessageToast({
        type: "error",
        title: "Failed to Send OTP",
        message:
          typeof err === "string"
            ? err
            : err?.message || "Unable to send OTP. Please try again.",
      });
    }
  };

  const handleBackToEmailRole = () => {
    setLoginStep(LOGIN_STEP.EMAIL_ROLE);
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    const fcmToken = await getFcmToken();
    try {
      const payload = {
        email: values.email,
        password: values.password,
        fcm_token: fcmToken,
        selected_role: preselectedRole,
      };
      console.log("payload:", payload);

      const res = await dispatch(loginUser(payload)).unwrap();

      // Clear old profile if any
      dispatch(clearUserProfile());

      // Fetch the fresh user profile immediately
      await dispatch(fetchUserProfile()).unwrap();

      showAppMessageToast({
        type: "success",
        title: "Login Successful",
        message: `Welcome, ${res?.user?.name || "User"}!`,
        navigate,
      });

      if (values.role === "seller") {
        const { redirectTo } = getSellerAccess(res.user);
        navigate(redirectTo);
      } else {
        const user = res.user;

        // Check email + phone verified (matching KycBanner pattern)
        const isEmailVerified =
          user?.email_verified_at !== null && user?.email_verified_at !== undefined;
        const isPhoneVerified =
          user?.phone_verified_at !== null && user?.phone_verified_at !== undefined;
        const isContactVerified = isEmailVerified && isPhoneVerified;

        // // OLD: kyc_verified check (commented out)
        // if (user?.kyc_verified === 0) {
        //   navigate("/kyc-verification", { state: { email: user.email } });
        // }

        if (!isContactVerified) {
          // Email or phone not verified yet — send to market
          navigate("/market");
        } else {
          // Fully verified buyer — go to dashboard
          navigate("/dashboard");
        }
      }
    } catch (err) {
      showAppMessageToast({
        type: "error",
        title: "Login Failed",
        message:
          typeof err === "string" ? err : err?.message || "Invalid credentials",
        navigate,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const [resendCooldown, setResendCooldown] = useState(0);
  const resendTimerRef = useRef(null);
  const handleResendVerificationLink = async (email) => {
    if (resendCooldown > 0) return;

    try {
      await dispatch(resendVerification({ email, type: "email" })).unwrap(); // 👈 replace with your actual thunk
      showAppMessageToast({
        type: "success",
        title: "Link Sent",
        message: "Verification link has been resent to your email.",
      });

      // Start 60 second cooldown
      setResendCooldown(60);
      if (resendTimerRef.current) clearInterval(resendTimerRef.current);
      resendTimerRef.current = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(resendTimerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      showAppMessageToast({
        type: "error",
        title: "Failed to Resend",
        message:
          typeof err === "string"
            ? err
            : err?.message || "Could not resend verification link.",
      });
    }
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (resendTimerRef.current) clearInterval(resendTimerRef.current);
    };
  }, []);

  return (
    <Box sx={{ display: "flex", height: "100vh" }}>
      {/* Left Banner Section - now takes up 2/3 of the screen on desktop */}
      {!isMobile && (
        <Grid
          item
          md={8}
          sx={{
            backgroundImage: `url(${roleContent.image})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            backgroundPosition: "center",
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "500px",
          }}
        >
          {/* Banner Overlay */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.55)",
              zIndex: 1,
            }}
          />

          {/* Banner Text Content */}
          <Box
            sx={{
              zIndex: 2,
              textAlign: "center",
              p: 4,
              minWidth: "786px",
              color: "white",
            }}
          >
            <Typography
              variant="body1"
              sx={{ fontSize: "1rem", opacity: 0.85, color: "#eee" }}
            >
              {selectedRole === "buyer" ? "For Buyers" : "For Sellers"}
            </Typography>

            <Typography
              variant="h3"
              component="h1"
              fontWeight={800}
              sx={{ my: 1, fontSize: "2.7rem", letterSpacing: "-0.5px" }}
            >
              {roleContent.title}
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: "#eee",
                fontSize: "1.05rem",
                maxWidth: "520px",
                mx: "auto",
                lineHeight: 1.6,
              }}
            >
              {roleContent.subtitle}
            </Typography>

            <Typography
              variant="body2"
              sx={{ mt: 1.5, color: "#ccc", fontStyle: "italic" }}
            >
              {roleContent.tagline}
            </Typography>
          </Box>
        </Grid>
      )}

      {/* Right Form Section - now takes up 1/3 of the screen on desktop */}
      <Grid
        item
        xs={12}
        md={4} // Changed from md={6} to md={4}
        component={Paper}
        elevation={0}
        square
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: customTheme.palette.background.default,
          flexGrow: 1,
          p: { xs: 2, md: 4 },
        }}
      >
        <Box
          sx={{
            my: { xs: 4, md: 8 },
            p: 4,
            width: "100%",
            maxWidth: 420, // Added maxWidth to prevent form from stretching on large screens
            borderRadius: "16px",
            boxShadow: "0 0 30px rgba(0,0,0,0.05)",
            backgroundColor: "white",
          }}
        >
          <Typography component="h2" variant="h5" fontWeight={600} mb={3}>
            Welcome Back
          </Typography>

          <Formik
            initialValues={{ email: "", password: "", role: preselectedRole }}
            enableReinitialize={true}
            validationSchema={getValidationSchema(loginStep)}
            onSubmit={handleSubmit}
            validateOnChange={true}
            validateOnBlur={true}
          >
            {({
              values,
              isValid,
              dirty,
              isSubmitting,
              setFieldValue,
              setTouched,
              errors,
              touched,
            }) => (
              <Form noValidate>
                {/* STEP 1: Email & Role */}
                {loginStep === LOGIN_STEP.EMAIL_ROLE && (
                  <>
                    <FormikTextField
                      name="email"
                      label="Email"
                      type="email"
                      placeholder="Enter email"
                    />

                    {/* <FormControl
                      component="fieldset"
                      margin="normal"
                      fullWidth
                      error={touched.role && !!errors.role}
                    >
                      <FormLabel
                        component="legend"
                        sx={{
                          color: "text.primary",
                          "&.Mui-focused": {
                            color: customTheme.palette.scraptradeYellow.dark,
                          },
                          mb: 1,
                        }}
                      >
                        Select Role
                      </FormLabel>
                      <RadioGroup
                        name="role"
                        value={values.role}
                        onChange={(e) => {
                          setFieldValue("role", e.target.value);
                          setSelectedRole(e.target.value);
                        }}
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          gap: 2,
                        }}
                      >
                        <FormControlLabel
                          value="buyer"
                          control={
                            <Radio
                              sx={{
                                color: customTheme.palette.scraptradeDark.main,
                                "&.Mui-checked": {
                                  color: customTheme.palette.scraptradeDark.main,
                                },
                              }}
                            />
                          }
                          label="Buyer"
                          sx={{
                            flex: 1,
                            m: 0,
                            height: "56px",
                            py: 0,
                            px: 2,
                            borderRadius: "8px",
                            backgroundColor:
                              values.role === "buyer"
                                ? customTheme.palette.scraptradeYellow.main
                                : "#f0f4f9",
                            border:
                              values.role === "buyer"
                                ? `2px solid ${customTheme.palette.scraptradeYellow.dark}`
                                : "2px solid transparent",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              backgroundColor:
                                values.role === "buyer"
                                  ? customTheme.palette.scraptradeYellow.main
                                  : "#e8edf2",
                            },
                            "& .MuiFormControlLabel-label": {
                              color: customTheme.palette.scraptradeDark.main,
                              fontWeight: values.role === "buyer" ? 600 : 400,
                            },
                          }}
                        />
                        <FormControlLabel
                          value="seller"
                          control={
                            <Radio
                              sx={{
                                color: customTheme.palette.scraptradeDark.main,
                                "&.Mui-checked": {
                                  color: customTheme.palette.scraptradeDark.main,
                                },
                              }}
                            />
                          }
                          label="Seller"
                          sx={{
                            flex: 1,
                            m: 0,
                            height: "56px",
                            py: 0,
                            px: 2,
                            borderRadius: "8px",
                            backgroundColor:
                              values.role === "seller"
                                ? customTheme.palette.scraptradeYellow.main
                                : "#f0f4f9",
                            border:
                              values.role === "seller"
                                ? `2px solid ${customTheme.palette.scraptradeYellow.dark}`
                                : "2px solid transparent",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              backgroundColor:
                                values.role === "seller"
                                  ? customTheme.palette.scraptradeYellow.main
                                  : "#e8edf2",
                            },
                            "& .MuiFormControlLabel-label": {
                              color: customTheme.palette.scraptradeDark.main,
                              fontWeight: values.role === "seller" ? 600 : 400,
                            },
                          }}
                        />
                      </RadioGroup>
                      {touched.role && errors.role && (
                        <FormHelperText>{errors.role}</FormHelperText>
                      )}
                    </FormControl> */}

                    <Button
                      type="button"
                      fullWidth
                      variant="contained"
                      sx={{
                        mt: 3,
                        mb: 2,
                        fontWeight: 600,
                        backgroundColor:
                          customTheme.palette.scraptradeYellow.main,
                        color: "#2D3A27",
                        "&:hover": {
                          backgroundColor:
                            customTheme.palette.scraptradeYellow.dark,
                        },
                      }}
                      onClick={() =>
                        handleContinueToMethod(values, { setTouched })
                      }
                    >
                      Continue
                    </Button>
                  </>
                )}

                {/* STEP 2: Choose Login Method */}
                {loginStep === LOGIN_STEP.CHOOSE_METHOD && (
                  <>
                    <FormikTextField
                      name="email"
                      label="Email"
                      type="email"
                      placeholder="Enter email"
                      readOnly={true}
                      onEditClick={handleBackToEmailRole}
                    />

                    {/* <Box sx={{ mt: 2, mb: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        Role: <strong>{values.role === "buyer" ? "Buyer" : "Seller"}</strong>
                      </Typography>
                    </Box> */}

                    <Button
                      type="button"
                      fullWidth
                      variant="contained"
                      sx={{
                        mt: 1,
                        mb: 2,
                        fontWeight: 600,
                        backgroundColor:
                          customTheme.palette.scraptradeYellow.main,
                        color: "#2D3A27",
                        "&:hover": {
                          backgroundColor:
                            customTheme.palette.scraptradeYellow.dark,
                        },
                      }}
                      onClick={handleChoosePassword}
                    >
                      Login with Password
                    </Button>

                    <Button
                      type="button"
                      fullWidth
                      variant="contained"
                      sx={{
                        mb: 2,
                        fontWeight: 600,
                        backgroundColor:
                          customTheme.palette.scraptradeYellow.main,
                        color: "#2D3A27",
                        "&:hover": {
                          backgroundColor:
                            customTheme.palette.scraptradeYellow.dark,
                        },
                      }}
                      onClick={() => handleChooseOTP(values)}
                    >
                      Login with OTP
                    </Button>

                    <Grid container justifyContent="center" sx={{ mt: 2 }}>
                      <Grid item>
                        <Link
                          component={RouterLink}
                          to="/forgot-password"
                          variant="body2"
                          sx={{
                            textDecoration: "none",
                            color: customTheme.palette.scraptradeYellow.main,
                            fontWeight: 600,
                          }}
                        >
                          Forgot Password?
                        </Link>
                      </Grid>
                    </Grid>

                    <Grid container justifyContent="center" sx={{ mt: 2 }}>
                      <Grid item>
                        <Typography variant="body2" component="span">
                          Don't have an account?{" "}
                        </Typography>
                        <Link
                          component={RouterLink}
                          to="/register"
                          state={{ role: preselectedRole }}
                          variant="body2"
                          sx={{
                            textDecoration: "none",
                            color: customTheme.palette.scraptradeYellow.main,
                            fontWeight: 600,
                          }}
                        >
                          Register
                        </Link>
                      </Grid>
                    </Grid>
                  </>
                )}

                {/* STEP 3: Password Login */}
                {loginStep === LOGIN_STEP.PASSWORD && (
                  <>
                    <FormikTextField
                      name="email"
                      label="Email"
                      type="email"
                      placeholder="Enter email"
                      readOnly={true}
                      onEditClick={handleBackToEmailRole}
                    />

                    <FormikTextField
                      name="password"
                      label="Password"
                      type="password"
                      placeholder="Enter password"
                    />

                    {auth.error && (
                      <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                        {(() => {
                          const errorMsg = auth.error.message || auth.error;
                          const isEmailVerifyError =
                            typeof errorMsg === "string" &&
                            errorMsg
                              .toLowerCase()
                              .includes("please verify your email");

                          return isEmailVerifyError ? (
                            <>
                              {errorMsg}{" "}
                              {resendCooldown > 0 ? (
                                <Typography
                                  component="span"
                                  variant="body2"
                                  sx={{
                                    color: "text.disabled",
                                    fontWeight: 600,
                                  }}
                                >
                                  Resend link ({resendCooldown}s)
                                </Typography>
                              ) : (
                                <Link
                                  component="button"
                                  type="button"
                                  variant="body2"
                                  onClick={() =>
                                    handleResendVerificationLink(values.email)
                                  }
                                  sx={{
                                    color:
                                      customTheme.palette.scraptradeYellow.dark,
                                    fontWeight: 600,
                                    textDecoration: "underline",
                                    cursor: "pointer",
                                    verticalAlign: "baseline",
                                    background: "none",
                                    border: "none",
                                    p: 0,
                                    transition: "color 0.2s ease",
                                    "&:hover": {
                                      color: customTheme.palette.primary.main,
                                      textDecoration: "underline",
                                    },
                                    "&:active": {
                                      color:
                                        customTheme.palette.scraptradeDark.main,
                                    },
                                  }}
                                >
                                  Resend link
                                </Link>
                              )}
                            </>
                          ) : (
                            errorMsg
                          );
                        })()}
                      </Typography>
                    )}

                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      sx={{
                        mt: 3,
                        mb: 2,
                        fontWeight: 600,
                        backgroundColor:
                          customTheme.palette.scraptradeYellow.main,
                        color: "#2D3A27",
                        "&:hover": {
                          backgroundColor:
                            customTheme.palette.scraptradeYellow.dark,
                        },
                      }}
                      disabled={isSubmitting}
                    >
                      Login
                    </Button>

                    <Grid container justifyContent="center" sx={{ mt: 2 }}>
                      <Grid item>
                        <Link
                          component={RouterLink}
                          to="/forgot-password"
                          variant="body2"
                          sx={{
                            textDecoration: "none",
                            color: customTheme.palette.scraptradeYellow.main,
                            fontWeight: 600,
                          }}
                        >
                          Forgot Password?
                        </Link>
                      </Grid>
                    </Grid>
                  </>
                )}

                {/* Common footer links - shown in all steps */}
                {loginStep !== LOGIN_STEP.CHOOSE_METHOD && (
                  <Grid container justifyContent="center" sx={{ mt: 2 }}>
                    <Grid item>
                      <Typography variant="body2" component="span">
                        Don't have an account?{" "}
                      </Typography>
                      <Link
                        component={RouterLink}
                        to="/register"
                        state={{ role: preselectedRole }}
                        variant="body2"
                        sx={{
                          textDecoration: "none",
                          color: customTheme.palette.scraptradeYellow.main,
                          fontWeight: 600,
                        }}
                      >
                        Register
                      </Link>
                    </Grid>
                  </Grid>
                )}
              </Form>
            )}
          </Formik>
        </Box>
      </Grid>
    </Box>
  );
};

// Main App component with theme provider
export default function App() {
  return (
    <ThemeProvider theme={customTheme}>
      <CssBaseline />
      <Login />
    </ThemeProvider>
  );
}
